<template>
  <div :title="$t('dashboard')">
      <h3> {{title}} </h3>
      <v-row>
        <v-col v-for="card in cards" :key="card.title" :lg="card.flex" sm="6" cols="12">
          <v-card max-width="344" v-if="eventCount">
            <v-card-title>{{ card.value }}</v-card-title>
            <v-card-text>{{ card.title }}</v-card-text>
          </v-card>
          <v-btn
            style="margin-top: 10px;"
            color="blue lighten-1"
            dark
            @click="resume.isPaused ? startListeningChannel() : stopListeningChannel()"
          >
            <v-icon
              color="white darken-1"
            >
              {{resume.icon}}
            </v-icon>
            {{resume.title}}
          </v-btn>
        </v-col>
        <v-col v-for="(counterCard, index) in counterCards"
          :class="{ 'offset-lg-2 offset-sm-0': index === 0}"
          :key="counterCard.title"
          :lg="counterCard.flex"
          sm="6"
          cols="12"
        >
          <v-card max-width="400" tile>
            <v-list-item three-line>
              <v-list-item-content>
                <v-list-item-title v-if="counterCard.title">
                  {{counterCard.title}}
                </v-list-item-title>
                <v-list-item-subtitle v-for="subtitle in counterCard.subtitles" :key="subtitle.title">
                  {{subtitle.title}}:
                  <b style="float: right;"> {{subtitle.value}}</b>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-card>
        </v-col>
      </v-row>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
      >
        <v-row>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="filters[0].value"
              :rules="[rules.rucLength, rules.rucFormat]"
              label="RIF"
              maxlength="20"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-combobox
              v-model="filters[2].value"
              :filter="filter"
              :hide-no-data="!search"
              :items="estatus"
              :search-input.sync="search"
              hide-selected
              label="Códigos de Estatus"
              multiple
              small-chips
            >
              <template v-slot:no-data>
                <v-list-item>
                  <span class="subheading">Crear</span>
                  <v-chip
                    :color="`${colors[nonce - 1]} lighten-3`"
                    small
                  >
                    {{ search }}
                  </v-chip>
                </v-list-item>
              </template>
              <template v-slot:selection="{ attrs, item, parent, selected }">
                <v-chip
                  v-if="item === Object(item)"
                  v-bind="attrs"
                  :color="`${item.color} lighten-3`"
                  :input-value="selected"
                  label
                  small
                >
                  <span class="pr-2">
                    {{ item.text }}
                  </span>
                  <v-icon
                    small
                    @click="parent.selectItem(item)"
                  >X</v-icon>
                </v-chip>
              </template>
              <template v-slot:item="{ index, item }">
                <v-text-field
                  v-if="editing === item"
                  v-model="editing.text"
                  autofocus
                  flat
                  background-color="transparent"
                  hide-details
                  solo
                  @keyup.enter="edit(index, item)"
                ></v-text-field>
                <v-chip
                  v-else
                  :color="`${item.color} lighten-3`"
                  dark
                  label
                  small
                >
                  {{ item.text }}
                </v-chip>
                <v-spacer></v-spacer>
                <v-list-item-action @click.stop>
                  <v-btn
                    icon
                    @click.stop.prevent="edit(index, item)"
                  >
                    <v-icon>{{ editing !== item ? 'mdi-pencil' : 'mdi-check' }}</v-icon>
                  </v-btn>
                </v-list-item-action>
              </template>
            </v-combobox>
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              :items="docTypes"
              :menu-props="{ maxHeight: '400' }"
              multiple v-on:change="setTypeFilter"
              item-text="name"
              item-value="code"
              attach
              chips
              default="00"
              dense
              label="Tipos de Documento"
            />
          </v-col>
        </v-row>
      </v-form>
    <apexchart
      ref="realtimeChart"
      type=line
      height=350
      :options="chartOptions"
      :series="series" />
      <v-simple-table dense class="elevation-1">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">RIF</th>
              <th class="text-left">Tipo de Documento</th>
              <th class="text-left">Numeración</th>
              <th class="text-left">Código de Estatus</th>
              <th class="text-left">Fecha de Recepción</th>
              <th class="text-left" v-if="columnSystem">Sistema</th>
              <th class="text-left">Tiempo de Procesamiento</th>
              <th class="text-left">Dirección IP</th>
            </tr>
          </thead>
          <tbody name="list-complete">
            <tr v-for="document in reverseDocuments" :key="document.session" :class="getRowClass(document)">
              <td>{{ document.rif }}</td>
              <td>{{ getTypeName(document.tipo) }}</td>
              <td>{{ document.numeracion }}</td>
              <td>{{ getCodeName(document.respuesta) }}</td>
              <td>{{ document.timestamp }}</td>
              <td v-if="columnSystem">{{ document.sistema }}</td>
              <td>{{ document.elapsed_time }}</td>
              <td>{{ document.ip }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import moment from 'moment';
import {
    mapGetters,
} from 'vuex';

let t = null;
let deleteInterval = null;

export default {
  metaInfo() {
      return { title: this.$t('dashboard') };
  },
  components: {
      apexchart: VueApexCharts,
  },
  props: {
      title: {
        type: String,
        required: true,
      },
      api: {
        type: String,
        required: true,
      },
      channel: {
        type: String,
        required: true,
      },
      event: {
        type: String,
        required: true,
      },
      metainfo: {
        type: String,
        required: true,
      },
      columnSystem: {
        type: Boolean,
        required: true,
      },
      eventCount: {
        type: Boolean,
        required: true,
      },
  },
  data() {
    return {
      filters: [
        {
          name: 'rif',
          value: '',
        },
        {
          name: 'emisor',
          value: null,
        },
        {
          name: 'codigo',
          value: [],
        },
        {
          name: 'ip',
          value: null,
        },
        {
          name: 'tipo',
          value: [],
        },
      ],
      colors: ['green', 'purple', 'indigo', 'cyan', 'teal', 'orange'],
      editing: null,
      index: -1,
      estatus: [
        { header: 'Seleccione una opción o cree una' },
        { text: 'Aceptados', color: 'blue' },
        { text: 'Rechazados', color: 'red' },
      ],
      nonce: 1,
      search: null,
      docTypes: [
        {
          code: '00',
          name: 'Todos',
        },
        {
          code: '01',
          name: 'Factura',
        },
        {
          code: '02',
          name: 'Notas de Crédito',
        },
        {
          code: '03',
          name: 'Notas de Débito',
        },
        {
          code: '04',
          name: 'Guías de Despacho',
        },
        {
          code: '05',
          name: 'Comprobante de Retención de IVA',
        },
      ],
      totalDocuments: 0,
      previousDocuments: 0,
      processedDocuments: 0,
      chartOptions: {
        chart: {
          animations: {
            enabled: true,
            easing: 'linear',
            dynamicAnimation: {
              speed: 300,
            },
          },
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
        },
        title: {
          text: 'Tiempos de Emisión',
          align: 'left',
        },

        xaxis: {
          type: 'datetime',
          range: 7500,
        },
        tooltip: {
          theme: 'dark',
          x: {
            formatter(val) {
              return moment(new Date(val)).format('HH:mm:ss');
            },
          },
        },
        yaxis: {
          decimalsInFloat: 2,
          labels: {
            formatter: (value) => `${value} ms`,
            offsetX: -10,
          },
        },
        legend: {
          show: true,
        },
      },
      resume: {
        title: 'Pausar',
        icon: 'mdi-pause',
        isPaused: false,
      },
      points: [],
      lastDate: 0,
      documents: [],
      times: [],
      codes: [],
      documentTypes: {
        '01': 'Factura',
        '02': 'Notas de Crédito',
        '03': 'Notas de Débito',
        '04': 'Guías de Despacho',
        '05': 'Comprobante de Retención de IVA',
      },
      rules: {
        ip: (v) => /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(v) || 'Ingrese una dirección IP válida.',
        rucLength: (v) => (!v || v.length === 11) || 'El campo debe tener de 11 caracteres.',
        rucFormat: (v) => /^[V,J,E,P,G]{1}[-][0-9]{9}$/.test(v) || 'El formato no corresponde.',
      },
      valid: false,
      // apiURL: '/api/settings/profileEnterprise',
      profileEnterprise: {},
    };
  },
  watch: {
    filters: {
      handler(val) {
        return val[2].value.map((v) => {
          if (typeof v === 'string') {
            v = {
              text: v,
              color: this.colors[this.nonce - 1],
            };
            this.estatus.push(v);
            this.nonce++;
          }
          return v;
        });
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),
    series() {
      return [{
        name: 'Tiempo de Procesamiento',
        data: this.points.slice(),
      }];
    },
    cards() {
      const today = moment(new Date()).format('DD-MM-Y hh:mm:ss a');
      return [
        {
          title: `Aceptados desde ${today}`,
          flex: 4,
          value: this.processedDocuments,
        },
      ];
    },
    counterCards() {
      return [
        {
          flex: 3,
          subtitles: [
            {
              title: 'Menos frecuente',
              value: this.leastViewedEvent,
            },
            {
              title: 'Más frecuente',
              value: this.mostViewedEvent,
            },
          ],
          title: 'Códigos de Estatus',
        },
        {
          flex: 2,
          subtitles: [
            {
              title: 'Tiempo mín.',
              value: this.minTime,
            },
            {
              title: 'Promedio',
              value: this.avgTime,
            },
            {
              title: 'Tiempo máx.',
              value: this.maxTime,
            },
          ],
        },
      ];
    },
    totalProcessed() {
      return (this.totalDocuments + this.processedDocuments).toLocaleString('de-DE');
    },
    avgTime() {
      if (this.times.length === 0) return 0;
      const reducer = (accumulator, currentValue) => (accumulator + currentValue);
      const time = parseFloat(this.times.reduce(reducer) / this.times.length).toLocaleString('de-DE', {
        maximumFractionDigits: 0,
      });
      return `${time} ms`;
    },
    minTime() {
      if (this.times.length === 0) return 0;
      const time = Math.min(...this.times).toLocaleString('de-DE', {
        maximumFractionDigits: 0,
      });
      return `${time} ms`;
    },
    maxTime() {
      if (this.times.length === 0) return 0;
      const time = Math.max(...this.times).toLocaleString('de-DE', {
        maximumFractionDigits: 0,
      });
      return `${time} ms`;
    },
    mostViewedEvent() {
      if (this.codes.length === 0) return 0;
      const counts = this.codes.reduce((a, c) => {
        a[c] = (a[c] || 0) + 1;
        return a;
      }, {});
      const maxCount = Math.max(...Object.values(counts));
      const mostFrequent = Object.keys(counts).filter((k) => counts[k] === maxCount);
      return mostFrequent.join(', ');
    },
    leastViewedEvent() {
      if (this.codes.length === 0) return 0;
      const counts = this.codes.reduce((a, c) => {
        a[c] = (a[c] || 0) + 1;
        return a;
      }, {});
      const minCount = Math.min(...Object.values(counts));
      const leastFrequent = Object.keys(counts).filter((k) => counts[k] === minCount);
      return leastFrequent.join(', ');
    },
    reverseDocuments() {
      return this.documents.slice().reverse();
    },
  },
  mounted() {
    // const enterpriseId = (this.taxpayer_id !== undefined && this.taxpayer_id !== null) ? this.taxpayer_id : this.user.enterprise_id;
    window.axios.get('/api/getIndicator')
    .then((result) => {
      this.processedDocuments = result.data.totalDocument;
    });
    this.points.push({
      x: 0,
      y: 0,
    });
    this.lastDate = new Date('10 Aug 2019 00:00:00 GMT').getTime();
    this.resetData();

    // const URL = `${this.apiURL}`;
    // this.URL = URL;
    // window.axios.get('/api/settings/profileEnterprise')
    //   .then((result) => {
    //     this.profileEnterprise = result.data;
    // });

    this.startListeningChannel();
  },
  methods: {
    startListeningChannel() {
      window.Echo.channel(this.channel)
        .listen(this.event, (e) => {
          // if (e.respuesta.indexOf('200') !== -1 && e.emisor.indexOf(this.profileEnterprise.ruc) !== -1) this.processedDocuments++;
          if (e.respuesta.indexOf('200') !== -1) this.processedDocuments++;
          if (this.filterDocument(e)) {
            this.addDocument(e);
            if (t !== null) {
              window.clearInterval(t);
            }
            t = window.setInterval(() => {
              this.lastDate += 300;
              const x = this.lastDate;
              const y = 0;
              this.points.push({
                x,
                y,
              });
              this.$refs.realtimeChart.updateSeries([{
                data: this.data,
              }]);
            }, 300);
            this.getNewSeries(this.lastDate, parseFloat(e.elapsed_time));
            this.$refs.realtimeChart.updateSeries([{
              data: this.data,
            }]);
          }
        });

      deleteInterval = window.setInterval(() => {
        this.resetData();
        const deletedTimes = Math.round(this.times.length * 0.10);
        this.times = this.times.splice(this.times.length - deletedTimes, this.times.length);
        this.codes = this.codes.splice(this.codes.length - deletedTimes, this.codes.length);
        this.$refs.realtimeChart.updateSeries([{
          data: this.data,
        }], false, true);
      }, 30150);
      this.resume.title = 'Pausar';
      this.resume.icon = 'mdi-pause';
      this.resume.isPaused = false;
    },
    stopListeningChannel() {
      window.Echo.channel(this.channel)
        .stopListening(this.event);
      window.clearInterval(t);
      window.clearInterval(deleteInterval);
      this.resume.title = 'Continuar';
      this.resume.icon = 'mdi-play';
      this.resume.isPaused = true;
    },
    filter(item, queryText, itemText) {
      if (item.header) return false;
      const hasValue = (val) => (val != null ? val : '');
      const text = hasValue(itemText);
      const query = hasValue(queryText);

      return text.toString()
        .toLowerCase()
        .indexOf(query.toString().toLowerCase()) > -1;
    },
    getNewSeries(baseval, yrange) {
      const newDate = baseval + 300;
      this.lastDate = newDate;
      this.points.push({
          x: newDate,
          y: yrange,
      });
    },
    resetData() {
      const deletedPoints = Math.round(this.points.length * 0.20);
      this.points = this.points.splice(this.points.length - deletedPoints, this.points.length);
    },
    addDocument(data) {
      this.documents.push(data);
      this.times.push(parseFloat(data.elapsed_time));
      data.respuesta.split(',').forEach((val) => this.codes.push(parseFloat(val)));
      if (this.documents.length > 10) { this.documents.splice(0, 1); }
    },
    getRowClass(document) {
      if (document.respuesta.indexOf('200') !== -1) { return 'success-row list-complete-item'; }
    },
    getTypeName(tipo) {
      return this.documentTypes[tipo];
    },
    getCodeName(tipo) {
      if (tipo.indexOf('200') !== -1) return 'Aceptado';
      return `Rechazado - ${tipo}`;
    },
    filterDocument(document) {
      const filters = this.filters.filter((filter) => (filter.value !== null && filter.value !== ''));
      let pass = true;
      filters.forEach((filter) => {
        if (filter.value instanceof Array && filter.value.length !== 0) {
          const filterString = filter.value.join(',');
          if (!filterString.includes(document[filter.name])) {
            pass = false;
          }
        }
        // else if (!document[filter.name].includes(filter.value)) {
        //   pass = false;
        // }
      });
      // FILTRO DE RUC
      if (this.filters[0].value.length > 0) {
        if (document.emisor !== this.filters[0].value) {
          pass = false;
        }
      }
      return pass;
    },
    setTypeFilter(e) {
      this.filters[4].value = Object.values(e);
      if (e.includes('00')) {
        this.filters[4].value = [];
      }
    },
  },
  beforeDestroy() {
    window.Echo.channel(this.channel)
      .stopListening('.process');
    window.clearInterval(t);
    window.clearInterval(deleteInterval);
  },
};
</script>

<style scoped>
  .success-row {
      color: #32a866;
      font-weight: bold;
  }

  .danger-row {
      color: red;
  }

  .apexcharts-tooltip,
  .apexcharts-tooltip.active,
  .apexcharts-xaxistooltip,
  .apexcharts-xaxistooltip.active,
  .apexcharts-marker {
      transition: none;
  }
</style>
